import { type IAugmentedJQuery, type IScope, IWindowService } from 'angular';

import template from './mx-shell-nav-tabs-level.html';

import { EventCodes } from '../../../App/model/event-codes';
import { ActionContextType, ActionsContext } from '../../../Shell/Pandora/model/action';

import type { EventService } from '../../../App/services/event-service';
import type { MxNavigationService } from '../../mx-navigation-service';
import type { MxActionManagerService } from '../../../Shell/Pandora/services/mx-action-manager-service';
import { NavigationItem } from '../../model/navigation-item';
import type { DashboardService } from '../../../Dashboard/dashboard.service';
import { DialogOpenerService } from '../../../Shell/PandoraSolutionBuilderAgent/services/dialog-opener-service';
import { DashboardConfigurationMode } from '../../../Dashboard/dashboard-page/dashboard-page.component';
import { PersonalizationService } from '../../../App/services/personalization-service';
import { I18n } from '../../../i18n/mx-internationalization';

interface NavigationItemWithVisibility extends NavigationItem {
	isHidden?: boolean;
}

interface SavedTabData {
	id: string;
	order: number;
	isHidden: boolean;
	title: string;
}

class ShellNavTabsLevelController {
	static readonly $inject = [
		'$window',
		'$element',
		'$scope',
		'mx.shell.EventService',
		'mx.shell.NavigationService',
		'mx.shell.ActionManagerService',
		'mx.sb.Dashboard.Service',
		'mx.SolutionBuilderAgent.DialogOpenerService',
		'mx.SolutionBuilderAgent.PersonalizationService',
		'mx.internationalization',
	];
	tabNavItems: NavigationItemWithVisibility[] = [];
	visibleTabs: NavigationItemWithVisibility[] = [];
	addDashboardSection: { visible: boolean; title: string; button: { title?: string; icon?: string; visible: boolean }; addingInProgress: boolean } = {
		visible: false,
		title: '',
		button: { visible: true },
		addingInProgress: false,
	};
	parentNavItem: NavigationItem | null | undefined;
	isDashboardLicensed = false;
	configurationMode: DashboardConfigurationMode = DashboardConfigurationMode.Disabled;
	public editingItemId: string | null = null;
	public editingTitle: string = '';
	/** id of the sole remaining visible tab, or null when multiple are visible */
	private _lastVisibleTabId: string | null = null;

	constructor(
		private readonly $window: IWindowService,
		private readonly $element: IAugmentedJQuery,
		private readonly $scope: IScope,
		private readonly eventService: EventService,
		private readonly navigationService: MxNavigationService,
		private readonly actionManagerService: MxActionManagerService,
		private readonly dashboardService: DashboardService,
		private readonly dialogOpenerService: ReturnType<typeof DialogOpenerService>,
		private readonly personalizationService: ReturnType<typeof PersonalizationService>,
		private readonly i18n: I18n
	) {
		this.eventService.subscribeTo(EventCodes.CurrentNavigationItemChanged, (_, { navigationItem }) => this.handleNavSwitching(navigationItem), $scope);
		this.eventService.subscribeTo(EventCodes.LayoutChanged, () => this.setAddDashboardButtonPosition(), $scope);
	}

	public draggedItem: NavigationItem | null = null;

	private getCurrentDashboardContext(): string | undefined {
		return this.parentNavItem?.id;
	}

	private canUserRename(item: NavigationItem): boolean {
		return !!item.isEditableDashboard;
	}

	public canCurrentUserRename(item: NavigationItem): boolean {
		return this.canUserRename(item);
	}

	private async saveTabPersonalization(): Promise<void> {
		try {
			const contextKey = this.getCurrentDashboardContext();

			const tabSettings = this.tabNavItems.map((item, index) => ({
				id: item.id,
				order: index,
				isHidden: item.isHidden || false,
				title: item.title,
			}));

			await this.personalizationService.saveDashboardTabsSettings(contextKey, tabSettings);
		} catch (error) {
			console.error('❌ Error saving dashboard tabs settings:', error);
		}
	}

	private async loadTabPersonalization(): Promise<void> {
		try {
			const contextKey = this.getCurrentDashboardContext();

			const savedTabs: SavedTabData[] = await this.personalizationService.getDashboardTabsSettings(contextKey);

			if (savedTabs && savedTabs.length > 0) {
				savedTabs.forEach((savedTab: SavedTabData) => {
					const tab = this.tabNavItems.find((item) => item.id === savedTab.id);
					if (tab) {
						tab.isHidden = savedTab.isHidden;
					}
				});

				const orderedTabs: NavigationItemWithVisibility[] = [];

				savedTabs
					.sort((a: SavedTabData, b: SavedTabData) => a.order - b.order)
					.forEach((savedTab: SavedTabData) => {
						const tab = this.tabNavItems.find((item) => item.id === savedTab.id);
						if (tab) {
							orderedTabs.push(tab);
						}
					});

				this.tabNavItems.forEach((tab) => {
					if (!orderedTabs.find((orderedTab) => orderedTab.id === tab.id)) {
						orderedTabs.push(tab);
					}
				});

				this.tabNavItems = orderedTabs;
			}
		} catch (error) {
			console.error('❌ Error loading dashboard tabs settings:', error);
		}
	}

	public onDragStart = (event: DragEvent, item: NavigationItem): void => {
		this.draggedItem = item;
		event.dataTransfer?.setData('text/plain', item.id);

		(event.target as HTMLElement).classList.add('mx-shell-nav-tabs-level__tab-being-dragged');

		const mdTab = (event.target as HTMLElement).closest('md-tab');
		if (mdTab) {
			mdTab.classList.add('mx-shell-nav-tabs-level__tab-being-dragged');
		}

		if (event.dataTransfer) {
			event.dataTransfer.effectAllowed = 'move';
		}
	};

	public onDragEnd = (event: DragEvent): void => {
		(event.target as HTMLElement).classList.remove('mx-shell-nav-tabs-level__tab-being-dragged');

		const mdTab = (event.target as HTMLElement).closest('md-tab');
		if (mdTab) {
			mdTab.classList.remove('mx-shell-nav-tabs-level__tab-being-dragged');
		}

		document.querySelectorAll('.mx-shell-nav-tabs-level__drag-over').forEach((el) => {
			el.classList.remove('mx-shell-nav-tabs-level__drag-over');
		});

		this.draggedItem = null;
	};

	public onDragEnter = (event: DragEvent, targetItem: NavigationItem): void => {
		if (this.draggedItem && this.draggedItem !== targetItem) {
			(event.currentTarget as HTMLElement).classList.add('mx-shell-nav-tabs-level__drag-over');
		}
	};

	public onDragLeave = (event: DragEvent): void => {
		const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
		if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
			(event.currentTarget as HTMLElement).classList.remove('mx-shell-nav-tabs-level__drag-over');
		}
	};

	public onDragOver = (event: DragEvent): boolean => {
		event.preventDefault();
		return false;
	};

	public onDrop = (event: DragEvent, targetItem: NavigationItem): boolean => {
		event.preventDefault();

		if (this.draggedItem && this.draggedItem !== targetItem) {
			const dragIndex = this.tabNavItems.indexOf(this.draggedItem);
			const dropIndex = this.tabNavItems.indexOf(targetItem);

			if (dragIndex > -1 && dropIndex > -1) {
				// Remove dragged item
				this.tabNavItems.splice(dragIndex, 1);
				// Insert at new position
				this.tabNavItems.splice(dropIndex, 0, this.draggedItem);

				this.saveTabPersonalization();
			}
		}

		return false;
	};

	public toggleVisibility = (item: NavigationItemWithVisibility, event: Event): void => {
		event.stopPropagation();
		event.preventDefault();

		item.isHidden = !item.isHidden;

		this.saveTabPersonalization();
		this.updateVisibleTabs();
	};

	public isToggleVisibilityDisabled(item: NavigationItemWithVisibility): boolean {
		return this._lastVisibleTabId === item.id;
	}

	public getVisibilityToggleTitle(item: NavigationItemWithVisibility): string {
		if (this.isToggleVisibilityDisabled(item)) {
			return this.i18n.get('shell.mx-shell-nav.tabManagement.cannotHideLastDashboard');
		}

		return item.isHidden ? this.i18n.get('shell.mx-shell-nav.tabManagement.showTab') : this.i18n.get('shell.mx-shell-nav.tabManagement.hideTab');
	}

	async $onInit() {
		this.isDashboardLicensed = await this.dashboardService.isLicensed();
		this.eventService.subscribeTo(
			EventCodes.DashboardPageConfigurationModeChanged,
			(_, payload = {}) => {
				this.$scope.$evalAsync(() => {
					const { configurationMode, isInitializing } = payload;
					this.configurationMode = configurationMode as unknown as DashboardConfigurationMode;
					if (!isInitializing) {
						this.updateVisibleTabs();
					}
				});
			},
			this.$scope
		);

		if (this.tabNavItems.length > 0) {
			await this.loadTabPersonalization();
			this.updateVisibleTabs();
		}
	}

	$postLink() {
		this.initAria();
	}

	public get isInEditMode(): boolean {
		return this.configurationMode === DashboardConfigurationMode.ExistWidgets;
	}

	public get dragHandleIconSrc(): string {
		return 'assets/images/modern_drag-indicator.svg';
	}

	public startRename(item: NavigationItem, $event: Event): void {
		$event.stopPropagation();
		$event.preventDefault(); // ✅ Prevent any tab selection changes

		this.editingItemId = item.id;
		this.editingTitle = item.title;

		this.setAddDashboardButtonPosition();

		setTimeout(() => {
			const input = document.querySelector('.mx-shell-nav-tabs-level__tab-rename-input') as HTMLInputElement;
			if (input) {
				input.focus();
			}
		}, 100);
	}

	public getInputStyle(item: NavigationItem): { width: string } {
		if (this.editingItemId === item.id) {
			const canvas = document.createElement('canvas');
			const context = canvas.getContext('2d')!;

			context.font = `1.4rem Roboto`;

			const textWidth = context.measureText(this.editingTitle || item.title).width;

			const totalWidth = Math.max(80, textWidth + 24);
			return { width: totalWidth + 'px' };
		}
		return { width: '120px' }; // Default width
	}

	public cancelRename(): void {
		if (this.editingItemId) {
			const currentItem = this.tabNavItems.find((item) => item.id === this.editingItemId);
			if (currentItem) {
				this.editingTitle = currentItem.title;
			}
		}

		this.editingItemId = null;

		this.setAddDashboardButtonPosition();
	}

	public handleRenameKeydown = (event: KeyboardEvent, item: NavigationItem): void => {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.saveRename(item);
		} else if (event.key === 'Escape') {
			event.preventDefault();
			this.cancelRename();
		}
	};

	public async saveRename(item: { id: string; title: string }): Promise<void> {
		if (!this.editingTitle.trim()) {
			console.warn('Cannot save empty title');
			return;
		}

		if (this.editingTitle.trim() === item.title) {
			this.cancelRename();
			return;
		}

		try {
			await this.dashboardService.updateDashboardTab(item.id, this.editingTitle.trim());

			item.title = this.editingTitle.trim();

			await this.navigationService.reload();

			this.cancelRename();
		} catch (error) {
			console.error('❌ Error renaming dashboard:', error);

			// Reset to original title on error
			this.editingTitle = item.title;
		}
	}

	public onRenameKeypress($event: KeyboardEvent): void {
		this.setAddDashboardButtonPosition();
		if ($event.key === 'Escape') {
			$event.preventDefault();
			this.cancelRename();
		}
	}

	public get isRenameValid(): boolean {
		return this.editingTitle?.trim().length > 0 && this.editingTitle.trim().length <= 100;
	}

	public get renameButtonDisabled(): boolean {
		const currentItem = this.tabNavItems.find((item) => item.id === this.editingItemId);
		return !this.isRenameValid || this.editingTitle.trim() === currentItem?.title;
	}

	private initAria() {
		this.$element.on('focus', 'md-tabs-canvas', (e: JQueryEventObject) => {
			if (e.target.tagName === 'MD-TABS-CANVAS') {
				setTimeout(() => {
					if (!$(e.relatedTarget).is('.mx-shell-nav-tabs-level__link')) {
						$(this.$element.find('.mx-shell-nav-tabs-level__link[tabindex=0]')[0]).trigger('focus');
					}
				});
			}
		});
	}

	private handleAriaNavItemSwitch() {
		setTimeout(() => {
			this.$element.find('md-tabs-canvas').removeAttr('tabindex');
			this.$element.find('[role=tablist]').attr('role', 'list').removeAttr('tabindex');
			this.$element.find('[role=tab]').attr('role', 'listitem').removeAttr('tabindex');
			this.$element.find('md-tabs-dummy-wrapper [tabindex=0]').removeAttr('tabindex');
			this.$element.find('md-tab-item').removeAttr('tabindex');
			this.$element.find('md-dummy-tab[tabindex]').removeAttr('tabindex');
			const activeElement = this.$window.document.activeElement as HTMLElement;
			if (activeElement?.tagName === 'MD-TAB-ITEM') {
				activeElement.blur();
			}
		});
	}

	async onDashboardNameKeypress(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			this.setAddingMode(false);
			return;
		}

		if (event.key === 'Enter' && this.addDashboardSection.title) {
			await this.addDashboard();
		}
	}

	async setAddDashboardSection() {
		this.addDashboardSection.visible = false;
		this.addDashboardSection.title = '';
		if (!this.tabNavItems?.every((n) => n?.implementationType === 'W')) {
			return;
		}

		const actions = this.actionManagerService.filterActions(await this.actionManagerService.getActions(new ActionsContext([], []), [ActionContextType.None]));
		const addDashboardAction = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.Add'));

		if (addDashboardAction) {
			this.addDashboardSection.button.title = addDashboardAction?.title;
			this.addDashboardSection.button.icon = addDashboardAction?.icon;
			this.addDashboardSection.visible = true;
		}

		this.setAddDashboardButtonPosition();
	}

	private readonly setAddDashboardButtonPosition = $.debounce(100, () => {
		const btn = this.$element.find('.mx-add-dashboard-section');
		const tabsWidth = this.$element.find('md-pagination-wrapper').width() ?? 0;
		const viewPortWidth = this.$element.width() ?? 0;
		if (viewPortWidth - tabsWidth > 60) {
			btn.css({ 'inset-inline-start': `${tabsWidth + 25}px`, 'inset-inline-end': `auto`, visibility: 'visible', 'margin-top': '-1px' });
		} else {
			btn.css({ 'inset-inline-start': '', 'inset-inline-end': '', visibility: 'visible', 'margin-top': '' });
		}
	}) as () => void;

	async addDashboard() {
		this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
			eventName: 'Action clicked',
			sender: 'ad537585-236b-c331-cabc-08dd03cb3113',
			senderTitle: 'UUX.Client.Dashboard.Add',
		});

		const title = this.addDashboardSection.title;
		this.setAddingMode(false);
		if (this.parentNavItem?.id && title) {
			const newDashboardNavigationItemId = await this.dashboardService.addDashboard(this.parentNavItem?.id, title);
			await this.navigationService.reload();
			await this.navigationService.setNavigationItemById(newDashboardNavigationItemId);

			this.scrollToActiveTab();
		}
	}

	private scrollToActiveTab(): void {
		setTimeout(() => {
			const activeTab = this.$element.find('md-tab-item.md-active')[0] as HTMLElement | undefined;
			activeTab?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
		}, 150);
	}

	setAddingMode(enabled: boolean) {
		if (!this.isDashboardLicensed) {
			this.dialogOpenerService.showLicenseUpgradeDialog();
			return;
		}
		this.addDashboardSection.addingInProgress = enabled;
		this.addDashboardSection.title = '';
		this.eventService.dispatchEvent(EventCodes.LayoutChanged);
		if (enabled) {
			setTimeout(() => $('.mx-add-dashboard-section--dashboard-name input').trigger('focus'));
		}
	}

	private readonly handleNavSwitching = $.debounce(100, (navigationItem: NavigationItem) => {
		if (!navigationItem) {
			return;
		}

		this.$scope.$applyAsync(async () => {
			const tabsLevelItem = this.navigationService.getTabsLevelItem(navigationItem);
			this.parentNavItem = tabsLevelItem;
			this.tabNavItems = this.parentNavItem?.children ?? [];
			await this.loadTabPersonalization();
			await this.setAddDashboardSection();
			this.updateVisibleTabs();
		});

		this.handleAriaNavItemSwitch();
	}) as (navigationItem: NavigationItem) => void;

	async handleItemClick(item: NavigationItem) {
		if (this.isInEditMode) {
			return;
		}
		this.eventService.dispatchEvent(EventCodes.BeforeNavigationItemClickHandler, item);
		this.eventService.dispatchEvent(EventCodes.TrackTelemetry, { eventName: `Navigation clicked`, sender: item.id, senderTitle: item.technicalName });

		let itemToBeOpen = item;
		while (!itemToBeOpen.route && itemToBeOpen.children?.length) {
			itemToBeOpen = itemToBeOpen.children[0];
		}

		await this.navigationService.setCurrentNavigationItem(itemToBeOpen);

		this.eventService.dispatchEvent(EventCodes.NavigationItemClicked, { navigationItem: itemToBeOpen });

		setTimeout(() => {
			$('#DWP-main-content').trigger('focus');
		}, 100);
	}

	private updateVisibleTabs(): void {
		if (this.isInEditMode) {
			this.visibleTabs = this.tabNavItems;
		} else {
			this.visibleTabs = this.tabNavItems.filter((item) => !item.isHidden);

			const activeChildTab = this.visibleTabs.find((tab) => tab.selected);

			if (this.parentNavItem?.activated) {
				this.parentNavItem.activated = false;
				setTimeout(() =>
					this.$scope.$apply(() => {
						this.parentNavItem!.activated = true;
					})
				);
			} else if (activeChildTab) {
				activeChildTab.selected = false;
				setTimeout(() =>
					this.$scope.$apply(() => {
						activeChildTab.selected = true;
					})
				);
			}
		}

		const visibleNonHidden = this.tabNavItems.filter((tab) => !tab.isHidden);
		this._lastVisibleTabId = visibleNonHidden.length === 1 ? visibleNonHidden[0].id : null;

		// remove tabindex from md-tab-item to fix double focus issue on tabs caused by angular material
		setTimeout(() => {
			this.$element.find('md-tab-item[tabindex]').removeAttr('tabindex');
			this.$element.find('md-tabs-canvas').removeAttr('tabindex');
		});

		this.scrollToActiveTab();
	}
}

export const mxShellNavTabsLevelComponent = {
	bindings: {},
	controller: ShellNavTabsLevelController,
	controllerAs: 'vm',
	template,
};
